import React, { useState, useCallback } from 'react';
import nzDriverLicenceFront from 'images/nz-driver-licence-front-v2.jpg';
import { Modal } from 'components/modal/Modal';
import { Button, Container, Text, Flex, Link } from 'basis';
import { DvsConsent } from 'components/idv-consent/DvsConsent';
import styled from '@emotion/styled';
import { minAgeMaxDate, minAge, maxAgeDate, maxAge, minExpiryDate } from './constants';
import {
  numbersOnlyPattern,
  relationshipStatusOptions,
  titleOptionsNZV2,
  validLicenceNumberPattern,
  validNamePattern,
  yesNoOptions,
} from '../_constants';

export const nameValidationMessages = {
  required: `Please enter your {{typeName}} as shown on your ID.`,
  minLength: 'Must have at least {{minLength}} characters',
  maxLength: 'Must have at most {{maxLength}} characters',
  invalid: 'Please enter a valid name',
};

const dateOfBirthValidationMessages = {
  required: 'Please enter your date of birth.',
  invalid: 'Please enter a valid date.',
  completeYear: 'Please enter the complete year YYYY.',
  maxDate: `Must be ${minAge} years of age or older.`,
  minDate: `The maximum age for a Gem Visa is ${maxAge} years old. Please update.`,
  dayError: 'Day must be within 1-31.',
  monthError: 'Month must be within 1-12.',
};

export const numberOfDependentsValidationMessages = {
  required: 'Please enter the number of dependant you financially support.',
  minValue: 'Your number of dependants must be {{minValue}} characters or more.',
  maxValue: 'Your number of dependants must be {{maxValue}} characters or less',
  invalid: 'Your number of dependants must be numeric characters only.',
};

const licenceNumberValidationMessages = {
  required: 'Please enter your licence number.',
  fixedLength: 'Must have {{fixedLength}} characters.',
  invalid: 'Invalid Licence Number.',
};

const versionValidationMessages = {
  required: 'Please enter the 3 digit version number.',
  fixedLength: 'Must have {{fixedLength}} characters.',
  invalid: 'Invalid Licence Version.',
};

const expiryDateValidationMessages = {
  required: 'Please enter your expiry date.',
  invalid: 'Please enter a valid date.',
  completeYear: 'Please enter the complete year YYYY.',
  minDate: 'Please enter a future date.',
  dayError: 'Day must be within 1-31.',
  monthError: 'Month must be within 1-12.',
};

const idNumberValidationMessages = {
  required: 'Please enter your ID number.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters.',
  invalid: 'Invalid ID Number.',
};

const nzResidencyNumberValidationMessages = {
  required: 'Please enter your NZ Residency number.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters.',
  invalid: 'Invalid ID Number.',
};

const validationMessages = {
  firstName: nameValidationMessages,
  middleName: nameValidationMessages,
  lastName: nameValidationMessages,
  numberOfDependents: numberOfDependentsValidationMessages,
  dateOfBirth: dateOfBirthValidationMessages,

  licenceNumber: licenceNumberValidationMessages,
  version: versionValidationMessages,
  idNumber: idNumberValidationMessages,
  nzResidencyNumber: nzResidencyNumberValidationMessages,
  expiryDate: expiryDateValidationMessages,
};

const maxAgeMinDate = maxAgeDate instanceof Date ? maxAgeDate.toISOString().split('T')[0] : maxAgeDate;

const fields = [
  {
    component: 'Select',
    name: 'title',
    label: 'Title',
    options: titleOptionsNZV2,
    testId: 'title',
    validate: {
      messages: {
        required: 'Please select a title.',
      },
    },
  },
  {
    component: 'Input',
    label: 'First name',
    name: 'firstName',
    testId: 'first-name',
    helpText: 'Legal name as shown on ID',
    validate: {
      props: { minLength: 2, maxLength: 15, typeName: 'first name', validPattern: validNamePattern },
      messages: validationMessages.firstName,
    },
  },
  {
    component: 'RadioTile',
    name: 'hasMiddleName',
    label: 'Do you have any middle name(s)?',
    options: yesNoOptions,
    showCircles: true,
    color: 'white',
    testId: 'has-middle-name',
  },
  {
    condition: values => values.hasMiddleName === 'yes',
    component: 'Input',
    name: 'middleName',
    label: 'Middle name(s)',
    testId: 'middle-name',
    helpText: 'Legal middle name as shown on ID',
    validate: {
      props: { minLength: 1, maxLength: 15, typeName: 'middle name', validPattern: validNamePattern },
      messages: validationMessages.middleName,
    },
  },
  {
    component: 'Input',
    name: 'lastName',
    label: 'Last name',
    testId: 'last-name',
    helpText: 'Legal name as shown on ID',
    validate: {
      props: { minLength: 2, maxLength: 23, typeName: 'last name', validPattern: validNamePattern },
      messages: validationMessages.lastName,
    },
  },
  {
    component: 'DatePicker',
    name: 'dateOfBirth',
    helpText: 'DD MM YYYYY',
    label: 'Date of birth',
    testId: 'date-of-birth',
    validate: {
      props: {
        maxDate: minAgeMaxDate,
        minDate: maxAgeMinDate,
        validPattern: '[1-9]+',
      },
      messages: validationMessages.dateOfBirth,
      component: 'DatePicker',
    },
  },
  {
    component: 'RadioTile',
    name: 'hasNzDriverLicence',
    label: 'Do you have a New Zealand Driver Licence?',
    options: yesNoOptions,
    showCircles: true,
    columns: 2,
    color: 'white',
    testId: 'has-nz-driver-licence',
  },
  {
    condition: values => values.hasNzDriverLicence === 'yes',
    component: 'Input',
    name: 'licenceNumber',
    label: 'Licence number',
    testId: 'licence-number',
    validate: {
      props: { fixedLength: 8, validPattern: validLicenceNumberPattern },
      messages: validationMessages.licenceNumber,
    },
    helpText: <LicenseNumberHelpText />,
  },
  {
    condition: values => values.hasNzDriverLicence === 'yes',
    component: 'Input',
    name: 'version',
    label: 'Version',
    testId: 'version',
    validate: {
      props: { fixedLength: 3, validPattern: numbersOnlyPattern },
      messages: validationMessages.version,
    },
    helpText: <LicenseVersionHelpText />,
  },
  {
    condition: values => values.hasNzDriverLicence === 'yes',
    component: 'DatePicker',
    name: 'expiryDate',
    label: 'Expiry date',
    testId: 'expiry-date',
    helpText: 'DD MM YYYY',
    day: true,
    validate: {
      props: { minDate: minExpiryDate, validPattern: numbersOnlyPattern },
      messages: validationMessages.expiryDate,
      component: 'DatePicker',
    },
  },
  {
    condition: values => values.hasNzDriverLicence === 'yes',
    component: 'CustomComponent',
    name: 'dvsConsent',
    children: DvsConsent,
  },
  {
    component: 'Select',
    name: 'relationshipStatus',
    label: 'Your relationship status',
    options: relationshipStatusOptions,
    testId: 'relationship-status',
    breakpoint: size => {
      const columns = ['md', 'lg', 'xl'].includes(size) ? 2 : 1;
      return { columns };
    },
    validate: {
      messages: {
        required: 'Please provide us your relationship status.',
      },
    },
  },
  {
    component: 'Input',
    variant: 'numeric',
    name: 'numberOfDependents',
    label: 'Number of financial dependants',
    helpText: 'Not including your partner, how many people do you financially support?',
    testId: 'number-of-dependents',
    validate: {
      props: {
        minValue: 0,
        maxValue: 99,
        validPattern: numbersOnlyPattern,
      },
      messages: validationMessages.numberOfDependents,
    },
  },
];

const StyledDriverLicenceImage = styled.img`
  max-width: 100%;

  ${props => `
    ${`@media (min-width: ${props.theme.screenSize.lg})`} {
      max-width: 295px;
    }
  `}
`;

function LicenseNumberHelpText() {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <span
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setIsOpen(!isOpen)}
        style={{
          textDecoration: 'underline',
          cursor: 'pointer',
          color: '#0156F5',
        }}
      >
        Licence number
      </span>{' '}
      is located on the front of the card, under the heading "Licence".
      <Modal isOpen={isOpen} onClose={onClose}>
        <Container padding="3" padding-md="8">
          <Text textStyle="heading4" margin="0 0 6 0" margin-md="0 0 7 0" testId="card-number-modal-title">
            Licence number
          </Text>

          <Flex height="100%" placeItems="center">
            <StyledDriverLicenceImage
              key={nzDriverLicenceFront}
              src={nzDriverLicenceFront}
              alt="NZ Driver Licence front"
            />
          </Flex>

          <Container padding="3 0 1 0">
            <Text textStyle="body1" margin="3 0">
              <b>Find your licence number</b>
            </Text>
            <Text textStyle="body1" margin="3 0">
              This is located on the front of the card, under the heading "Licence".
            </Text>
          </Container>

          <Container padding="3 0 1 0">
            <Text textStyle="body1" margin="3 0">
              <b>Licence number explained</b>
            </Text>
            <Text textStyle="body1" margin="3 0">
              The card number is a unique number and will remain the same each time a new card is issued.
            </Text>
            <Text textStyle="body1" margin="3 0">
              For more information visit the{' '}
              <Link href="https://nzta.govt.nz/driver-licences/getting-a-licence/your-driver-licence-explained" newTab>
                NZ Transport Agency - Your driver licence explained
              </Link>
              .
            </Text>
          </Container>

          <Flex placeItems="center">
            <Container padding="2">
              <Button width="160px" onClick={onClose}>
                Close
              </Button>
            </Container>
          </Flex>
        </Container>
      </Modal>
    </>
  );
}

function LicenseVersionHelpText() {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <span
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setIsOpen(!isOpen)}
        style={{
          textDecoration: 'underline',
          cursor: 'pointer',
          color: '#0156F5',
        }}
      >
        Version number
      </span>{' '}
      is located on the front of the card, under the heading "Version".
      <Modal isOpen={isOpen} onClose={onClose}>
        <Container padding="3" padding-md="8">
          <Text textStyle="heading4" margin="0 0 6 0" margin-md="0 0 7 0" testId="card-number-modal-title">
            Version number
          </Text>

          <Flex height="100%" placeItems="center">
            <StyledDriverLicenceImage
              key={nzDriverLicenceFront}
              src={nzDriverLicenceFront}
              alt="NZ Driver Licence front"
            />
          </Flex>

          <Container padding="3 0 1 0">
            <Text textStyle="body1" margin="3 0">
              <b>Find your version number</b>
            </Text>
            <Text textStyle="body1" margin="3 0">
              This is located on the front of the card, under the heading “Version”.{' '}
            </Text>
          </Container>

          <Container padding="3 0 1 0">
            <Text textStyle="body1" margin="3 0">
              <b>Version number explained</b>
            </Text>
            <Text textStyle="body1" margin="3 0">
              The version number changes each time a new card is issued.
            </Text>
            <Text textStyle="body1" margin="3 0">
              For more information visit the{' '}
              <Link href="https://nzta.govt.nz/driver-licences/getting-a-licence/your-driver-licence-explained" newTab>
                NZ Transport Agency - Your driver licence explained
              </Link>
              .
            </Text>
          </Container>

          <Flex placeItems="center">
            <Container padding="2">
              <Button width="160px" onClick={onClose}>
                Close
              </Button>
            </Container>
          </Flex>
        </Container>
      </Modal>
    </>
  );
}

export const personalDetails = fields;
